import {
  useDeferredValue,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { bindTypeaheadReposition, computeFixedTypeaheadStyle } from '../../utils/typeaheadPosition';

export type DemTypeaheadOption = {
  value: string;
  label: string;
  blocked?: boolean;
  /** Item table columns. */
  description?: string;
  inventory?: number | string | null;
  uom?: string;
  packing?: string;
  /** Customer table columns. */
  customerName?: string;
  address?: string;
  address2?: string;
  postal?: string;
};

export type DemTypeaheadTableMode = false | true | 'item' | 'customer' | 'location';

type DemTypeaheadProps = {
  value: string;
  onChange: (value: string) => void;
  /** Fired when user picks from list or leaves the field (not on every keystroke). */
  onCommit?: (value: string) => void;
  /** When false, typing only updates the local field until commit. Default true. */
  commitOnType?: boolean;
  options: DemTypeaheadOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  maxVisible?: number;
  ariaLabel?: string;
  /**
   * Table dropdown:
   * - true / 'item' → Item code / Description / Inventory / Base UOM / Packing
   * - 'customer' → Customer no. / Name / Address / Address 2 / Postal
   * - 'location' → Place / Type (area, mall, MRT)
   */
  tableMode?: DemTypeaheadTableMode;
};

function formatInventory(value: number | string | null | undefined): string {
  if (value == null || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  return Number.isInteger(n) ? String(n) : n.toLocaleString('en-SG', { maximumFractionDigits: 2 });
}

function resolveTableMode(tableMode: DemTypeaheadTableMode): 'item' | 'customer' | 'location' | null {
  if (tableMode === true || tableMode === 'item') return 'item';
  if (tableMode === 'customer') return 'customer';
  if (tableMode === 'location') return 'location';
  return null;
}

function isSkipKeyword(text: string): boolean {
  return ['IGNORE', 'SKIP', 'N/A', 'NONE'].includes(text.trim().toUpperCase());
}

export function DemTypeahead({
  value,
  onChange,
  onCommit,
  commitOnType = true,
  options,
  placeholder,
  disabled = false,
  className = '',
  inputClassName = 'dem-typeahead__input',
  maxVisible = 80,
  ariaLabel,
  tableMode = false,
}: DemTypeaheadProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLElement>(null);
  const focusedRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);
  const [highlight, setHighlight] = useState(0);
  const [listStyle, setListStyle] = useState<CSSProperties>({});
  const justCommittedRef = useRef(false);
  const resolvedTable = resolveTableMode(tableMode);
  const deferredQuery = useDeferredValue(query);

  // Do not clobber in-progress typing when parent re-renders.
  useEffect(() => {
    if (focusedRef.current) return;
    setQuery(value);
  }, [value]);

  const filtered = useMemo(() => {
    const q = deferredQuery.trim().toLowerCase();
    if (!q) return options.slice(0, maxVisible);
    return options
      .filter((opt) => {
        const hay = [
          opt.value,
          opt.label,
          opt.description ?? '',
          opt.customerName ?? '',
          opt.address ?? '',
          opt.address2 ?? '',
          opt.postal ?? '',
          opt.uom ?? '',
          opt.packing ?? '',
        ]
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      })
      .slice(0, maxVisible);
  }, [options, deferredQuery, maxVisible]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  useEffect(() => {
    setHighlight(0);
  }, [deferredQuery, open]);

  const updateListPosition = () => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const minWidth =
      resolvedTable === 'customer' ? 780 : resolvedTable === 'location' ? 360 : resolvedTable ? 720 : 280;
    const maxWidth =
      resolvedTable === 'customer' ? 980 : resolvedTable === 'location' ? 480 : resolvedTable ? 920 : 480;
    setListStyle(
      computeFixedTypeaheadStyle(rect, {
        minWidth,
        maxWidth,
        preferBelowPx: 160,
        maxHeightCap: 420,
        gap: 4,
        zIndex: 4200,
      }),
    );
  };

  useEffect(() => {
    if (!open) return;
    updateListPosition();
    return bindTypeaheadReposition(updateListPosition);
    // Reposition on open / table mode only — not on every keystroke (was blocking typing).
  }, [open, resolvedTable]);

  const commitValue = (next: string) => {
    justCommittedRef.current = true;
    onChange(next);
    onCommit?.(next);
    setQuery(next);
    setOpen(false);
  };

  const selectOption = (opt: DemTypeaheadOption) => {
    commitValue(opt.value);
  };

  const flushTypedValue = () => {
    if (justCommittedRef.current) {
      justCommittedRef.current = false;
      return;
    }
    if (commitOnType) return;
    const trimmed = query.trim();
    if (!trimmed) {
      if (value.trim()) {
        onChange('');
        onCommit?.('');
      }
      return;
    }
    const next = isSkipKeyword(trimmed) ? trimmed.toUpperCase() : trimmed;
    if (next === value) return;
    onChange(next);
    onCommit?.(next);
    setQuery(next);
  };

  const handleInputChange = (next: string) => {
    setQuery(next);
    setOpen(true);
    if (commitOnType) {
      onChange(next);
      return;
    }
    // IGNORE / SKIP must apply as soon as fully typed (Confirm click must not race blur).
    if (isSkipKeyword(next)) {
      commitValue(next.trim().toUpperCase());
    }
  };

  const handleBlur = () => {
    focusedRef.current = false;
    flushTypedValue();
    setOpen(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      setOpen(true);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(0, filtered.length - 1)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const typed = query.trim();
      // IGNORE / SKIP must win over any highlighted dropdown row.
      if (isSkipKeyword(typed)) {
        commitValue(typed.toUpperCase());
        return;
      }
      const opt = filtered[highlight];
      if (opt) selectOption(opt);
      else commitValue(typed);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const cell = (text: string | undefined | null, fallback = '—') => {
    const trimmed = text?.trim();
    return trimmed || fallback;
  };

  return (
    <div ref={rootRef} className={`dem-typeahead${className ? ` ${className}` : ''}`}>
      <input
        ref={inputRef}
        type="text"
        className={inputClassName}
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        role="combobox"
        autoComplete="off"
        spellCheck={false}
        onFocus={() => {
          focusedRef.current = true;
          setOpen(true);
          updateListPosition();
        }}
        onChange={(e) => handleInputChange(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      {open && !disabled
        ? createPortal(
            resolvedTable === 'item' ? (
              <div
                ref={(node) => {
                  listRef.current = node;
                }}
                id={listId}
                className="dem-typeahead__list dem-typeahead__list--fixed dem-typeahead__list--table dem-typeahead__list--table-item"
                style={listStyle}
                role="listbox"
              >
                <div className="dem-typeahead__table-head dem-typeahead__table-head--item" aria-hidden>
                  <span>Item code</span>
                  <span>Description</span>
                  <span>Inventory</span>
                  <span>Base UOM</span>
                  <span>Packing</span>
                </div>
                <div className="dem-typeahead__table-body">
                  {filtered.length === 0 ? (
                    <p className="dem-typeahead__empty">
                      {isSkipKeyword(query) ? 'Press Enter to ignore this line' : 'No matches'}
                    </p>
                  ) : (
                    filtered.map((opt, index) => (
                      <button
                        key={`${opt.value}-${index}`}
                        type="button"
                        role="option"
                        aria-selected={index === highlight}
                        className={`dem-typeahead__table-row dem-typeahead__table-row--item${
                          index === highlight ? ' dem-typeahead__table-row--active' : ''
                        }${opt.blocked ? ' dem-typeahead__table-row--blocked' : ''}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectOption(opt)}
                        onMouseEnter={() => setHighlight(index)}
                      >
                        <span className="dem-typeahead__table-code mono" title={opt.value}>
                          {opt.value}
                        </span>
                        <span
                          className="dem-typeahead__table-desc"
                          title={opt.description || opt.label}
                        >
                          {opt.description || opt.label}
                        </span>
                        <span className="dem-typeahead__table-inv mono">
                          {formatInventory(opt.inventory)}
                        </span>
                        <span className="dem-typeahead__table-uom">{cell(opt.uom)}</span>
                        <span className="dem-typeahead__table-packing" title={opt.packing || undefined}>
                          {cell(opt.packing)}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            ) : resolvedTable === 'customer' ? (
              <div
                ref={(node) => {
                  listRef.current = node;
                }}
                id={listId}
                className="dem-typeahead__list dem-typeahead__list--fixed dem-typeahead__list--table dem-typeahead__list--table-customer"
                style={listStyle}
                role="listbox"
              >
                <div className="dem-typeahead__table-head dem-typeahead__table-head--customer" aria-hidden>
                  <span>Customer no.</span>
                  <span>Customer name</span>
                  <span>Address</span>
                  <span>Address 2</span>
                  <span>Postal code</span>
                </div>
                <div className="dem-typeahead__table-body">
                  {filtered.length === 0 ? (
                    <p className="dem-typeahead__empty">No matches</p>
                  ) : (
                    filtered.map((opt, index) => (
                      <button
                        key={`${opt.value}-${index}`}
                        type="button"
                        role="option"
                        aria-selected={index === highlight}
                        className={`dem-typeahead__table-row dem-typeahead__table-row--customer${
                          index === highlight ? ' dem-typeahead__table-row--active' : ''
                        }${opt.blocked ? ' dem-typeahead__table-row--blocked' : ''}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectOption(opt)}
                        onMouseEnter={() => setHighlight(index)}
                      >
                        <span className="dem-typeahead__table-code mono" title={opt.value}>
                          {opt.value}
                        </span>
                        <span
                          className="dem-typeahead__table-name"
                          title={opt.customerName || opt.label}
                        >
                          {cell(opt.customerName || opt.label)}
                        </span>
                        <span className="dem-typeahead__table-address" title={opt.address || undefined}>
                          {cell(opt.address)}
                        </span>
                        <span className="dem-typeahead__table-address2" title={opt.address2 || undefined}>
                          {cell(opt.address2)}
                        </span>
                        <span className="dem-typeahead__table-postal mono">{cell(opt.postal)}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            ) : resolvedTable === 'location' ? (
              <div
                ref={(node) => {
                  listRef.current = node;
                }}
                id={listId}
                className="dem-typeahead__list dem-typeahead__list--fixed dem-typeahead__list--table dem-typeahead__list--table-location"
                style={listStyle}
                role="listbox"
              >
                <div className="dem-typeahead__table-head dem-typeahead__table-head--location" aria-hidden>
                  <span>Place</span>
                  <span>Type</span>
                </div>
                <div className="dem-typeahead__table-body">
                  {filtered.length === 0 ? (
                    <p className="dem-typeahead__empty">No matches</p>
                  ) : (
                    filtered.map((opt, index) => (
                      <button
                        key={`${opt.value}-${index}`}
                        type="button"
                        role="option"
                        aria-selected={index === highlight}
                        className={`dem-typeahead__table-row dem-typeahead__table-row--location${
                          index === highlight ? ' dem-typeahead__table-row--active' : ''
                        }`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectOption(opt)}
                        onMouseEnter={() => setHighlight(index)}
                      >
                        <span className="dem-typeahead__table-name" title={opt.label}>
                          {opt.label}
                        </span>
                        <span className="dem-typeahead__table-uom">{cell(opt.description)}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <ul
                ref={(node) => {
                  listRef.current = node;
                }}
                id={listId}
                className="dem-typeahead__list dem-typeahead__list--fixed"
                style={listStyle}
                role="listbox"
              >
                {filtered.length === 0 ? (
                  <li className="dem-typeahead__empty">No matches</li>
                ) : (
                  filtered.map((opt, index) => (
                    <li key={`${opt.value}-${opt.label}`}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={index === highlight}
                        className={`dem-typeahead__option${
                          index === highlight ? ' dem-typeahead__option--active' : ''
                        }${opt.blocked ? ' dem-typeahead__option--blocked' : ''}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectOption(opt)}
                        onMouseEnter={() => setHighlight(index)}
                      >
                        {opt.label}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            ),
            document.body,
          )
        : null}
    </div>
  );
}
